import { useState, useEffect, useCallback, useRef } from 'react';
import { useUrlParam, useUrlPanelSync } from '../../../hooks/useUrlParam';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faPlus, faPen, faTrash, faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Client } from '../../../context/ClientContext';
import { supabase } from '../../../lib/supabase';
import {
  listKbFiles,
  getKbFile,
  createKbFile,
  updateKbFile,
  deleteKbFile,
  searchKbFiles,
  type ClientKbFile,
  type ClientKbKind,
  type ClientKbSearchHit,
  type ClientKbStatus,
} from '../../../lib/kb';
import { Badge, Button, Card, Input, KebabMenu, Popover, PulseDot, SidePanel } from '../../ui';
import type { BadgeTone } from '../../ui';
import DeleteConfirmationModal from './functions/DeleteConfirmationModal';

type KbRow = ClientKbFile | ClientKbSearchHit;

interface KnowledgeSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

type PulseTone = 'up' | 'warn' | 'info' | 'down' | 'idle';

const KB_KINDS: { value: ClientKbKind; label: string; tone: BadgeTone; pulse: PulseTone }[] = [
  { value: 'canon', label: 'Canon', tone: 'accent', pulse: 'info' },
  { value: 'transcript', label: 'Transcript', tone: 'info', pulse: 'info' },
  { value: 'loom', label: 'Loom', tone: 'warn', pulse: 'warn' },
  { value: 'note', label: 'Note', tone: 'neutral', pulse: 'idle' },
];

const KB_STATUSES: { value: ClientKbStatus; label: string; tone: BadgeTone; pulse: PulseTone }[] = [
  { value: 'current', label: 'Current', tone: 'up', pulse: 'up' },
  { value: 'superseded', label: 'Superseded', tone: 'idle', pulse: 'idle' },
  { value: 'unknown', label: 'Unknown', tone: 'warn', pulse: 'warn' },
];

const EMPTY_FORM = {
  title: '',
  body: '',
  as_of: '',
  kind: 'note' as ClientKbKind,
  status: 'current' as ClientKbStatus,
};

function todayAsOf(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Denver',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function formatAsOf(asOf: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(asOf);
  if (!match) return asOf;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function kindMeta(kind: ClientKbKind) {
  return KB_KINDS.find((k) => k.value === kind) ?? KB_KINDS[3];
}

function statusMeta(status: ClientKbStatus) {
  return KB_STATUSES.find((s) => s.value === status) ?? KB_STATUSES[0];
}

function FieldMenu<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; tone: BadgeTone; pulse: PulseTone }[];
  onChange: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selected = options.find((o) => o.value === value) ?? options[0];

  return (
    <div>
      <div className="t-eyebrow" style={{ marginBottom: '7px' }}>{label}</div>
      <span style={{ position: 'relative', display: 'inline-flex', width: '100%' }}>
        <button
          ref={buttonRef}
          type="button"
          className="pressable"
          title={`Change ${label.toLowerCase()}`}
          aria-label={`Change ${label.toLowerCase()}`}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            width: '100%',
            height: 'var(--control-md)',
            padding: '0 11px 0 12px',
            background: 'var(--surface-raised)',
            border: '1px solid ' + (open ? 'var(--accent)' : 'var(--border-hairline)'),
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <Badge tone={selected.tone} dot>{selected.label}</Badge>
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{
              fontSize: '10px',
              color: 'var(--text-faint)',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform var(--dur-base) var(--ease-out)',
            }}
          />
        </button>
        <Popover
          open={open}
          anchorEl={buttonRef.current}
          onClose={() => setOpen(false)}
          matchWidth
          offset={6}
          minWidth={148}
          role="menu"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="hover-wash pressable"
              onClick={() => {
                setOpen(false);
                if (option.value !== value) onChange(option.value);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                padding: '7px 10px',
                border: 'none',
                background: 'transparent',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                fontWeight: (option.value === value ? 'var(--weight-medium)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
                color: 'var(--text-body)',
              }}
            >
              <PulseDot tone={option.pulse} pulse={false} />
              {option.label}
            </button>
          ))}
        </Popover>
      </span>
    </div>
  );
}

export default function KnowledgeSection({ client, expandedSection }: KnowledgeSectionProps) {
  const [files, setFiles] = useState<KbRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KbRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const fetchFiles = useCallback(async () => {
    if (!client?.id) return;

    try {
      setIsLoading(true);
      setError(null);
      if (debouncedQuery) {
        setFiles(await searchKbFiles(client.id, debouncedQuery));
      } else {
        setFiles(await listKbFiles(client.id));
      }
    } catch (err) {
      console.error('Error fetching knowledge files:', err);
      setError('Failed to load knowledge');
    } finally {
      setIsLoading(false);
    }
  }, [client?.id, debouncedQuery]);

  useEffect(() => {
    if (expandedSection === 'knowledge') {
      fetchFiles();
    }
  }, [expandedSection, fetchFiles]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, as_of: todayAsOf() });
    setEditingId(null);
    setError(null);
    setIsPanelOpen(true);
  };

  const handleEdit = async (row: KbRow) => {
    try {
      setError(null);
      const full = 'body' in row ? (row as ClientKbFile) : await getKbFile(row.id);
      if (!full) {
        setError('Failed to load file');
        return;
      }
      setForm({
        title: full.title,
        body: full.body,
        as_of: full.as_of,
        kind: full.kind,
        status: full.status,
      });
      setEditingId(full.id);
      setIsPanelOpen(true);
    } catch (err) {
      console.error('Error loading knowledge file:', err);
      setError('Failed to load file');
    }
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setIsPanelOpen(false);
    setEditingId(null);
    setError(null);
  };

  // The open file is ?file=<id>: a row click sets it, closing clears it, and the URL drives the panel.
  const [urlFile, setUrlFile] = useUrlParam('file');
  useUrlPanelSync(urlFile, isPanelOpen ? editingId : null, true, (id) => void handleEdit({ id } as KbRow), handleCancel);
  const closePanel = () => {
    handleCancel();
    if (urlFile) setUrlFile(null);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!form.as_of.trim()) {
      setError('As of date is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (editingId) {
        await updateKbFile(editingId, {
          title: form.title.trim(),
          body: form.body,
          as_of: form.as_of,
          kind: form.kind,
          status: form.status,
        });
        setSuccess('File updated');
      } else {
        const { data: auth } = await supabase.auth.getUser();
        await createKbFile({
          client_id: client.id,
          title: form.title.trim(),
          body: form.body,
          as_of: form.as_of,
          kind: form.kind,
          status: form.status,
          added_by: auth.user?.id ?? null,
        });
        setSuccess('File added');
      }

      setForm(EMPTY_FORM);
      setIsPanelOpen(false);
      setEditingId(null);
      if (urlFile) setUrlFile(null);
      await fetchFiles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving knowledge file:', err);
      setError('Failed to save file. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    setError(null);
    try {
      await deleteKbFile(deleteTarget.id);
      setSuccess('File deleted');
      setDeleteTarget(null);
      await fetchFiles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting knowledge file:', err);
      setError('Failed to delete file.');
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  if (expandedSection !== 'knowledge') return null;

  const searching = Boolean(debouncedQuery);
  const countLabel = isLoading
    ? 'Loading…'
    : searching
      ? files.length > 0
        ? `${files.length} match${files.length === 1 ? '' : 'es'}`
        : 'No matches'
      : files.length > 0
        ? `${files.length} file${files.length === 1 ? '' : 's'}`
        : 'No files yet';

  return (
    <Card
      eyebrow="Knowledge"
      title={countLabel}
      actions={
        <Button size="sm" variant="secondary" onClick={openCreate}>
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: '12px', marginRight: '7px' }} />
          Add File
        </Button>
      }
    >
      <div style={{ marginBottom: '14px' }}>
        <Input
          icon="search"
          fieldSize="sm"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title or body…"
          aria-label="Search knowledge"
        />
      </div>

      {error && !isPanelOpen && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="hover-underline"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 'var(--text-xs)',
              flexShrink: 0,
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-up-soft)',
            border: '1px solid rgba(86, 162, 104, 0.25)',
            color: 'var(--status-up)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <FontAwesomeIcon icon={faCheck} style={{ fontSize: '11px', marginRight: '8px' }} />
          {success}
        </div>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '28px 0' }}>
          <div
            className="animate-spin w-7 h-7 border-2 rounded-full"
            style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
          />
        </div>
      ) : files.length === 0 ? (
        <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          <FontAwesomeIcon icon={faBook} style={{ fontSize: '22px', color: 'var(--text-faint)' }} />
          <p style={{ margin: '12px 0 16px', fontSize: 'var(--text-base)' }}>
            {searching
              ? 'No knowledge files match that search.'
              : 'Keep canon, transcripts, Looms, and notes on this client — context, never the work itself.'}
          </p>
          {!searching ? (
            <Button size="sm" variant="secondary" onClick={openCreate}>
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: '12px', marginRight: '7px' }} />
              Add File
            </Button>
          ) : null}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {files.map((file) => {
            const kind = kindMeta(file.kind);
            const status = statusMeta(file.status);
            return (
              <div
                key={file.id}
                className="hover-wash"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px',
                  margin: '0 -10px',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <FontAwesomeIcon
                  icon={faBook}
                  style={{ fontSize: '13px', width: '16px', color: 'var(--text-faint)', flexShrink: 0 }}
                />
                <button
                  type="button"
                  onClick={() => setUrlFile(file.id)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span
                    className="t-num"
                    style={{
                      display: 'block',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                      color: 'var(--text-body)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {file.title}
                  </span>
                  <span
                    className="t-num flex flex-wrap items-center md:block"
                    style={{
                      gap: '4px 8px',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-body)',
                      marginTop: '3px',
                      fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                    }}
                  >
                    <span>as of {formatAsOf(file.as_of)}</span>
                    {/* Phones: the badges ride under the title, which otherwise gets a dozen characters */}
                    <span className="inline-flex md:hidden" style={{ gap: '6px' }}>
                      <Badge tone={kind.tone}>{kind.label}</Badge>
                      <Badge tone={status.tone} dot>{status.label}</Badge>
                    </span>
                  </span>
                </button>
                <span className="hidden md:contents">
                  <Badge tone={kind.tone}>{kind.label}</Badge>
                  <Badge tone={status.tone} dot>{status.label}</Badge>
                </span>
                <KebabMenu
                  label="Knowledge actions"
                  actions={[
                    { label: 'Edit File', icon: faPen, onClick: () => setUrlFile(file.id) },
                    { label: 'Delete', icon: faTrash, danger: true, onClick: () => setDeleteTarget(file) },
                  ]}
                />
              </div>
            );
          })}
        </div>
      )}

      <SidePanel
        open={isPanelOpen}
        onClose={closePanel}
        width={520}
        eyebrow={`Knowledge · ${client.name}`}
        title={editingId ? 'Edit File' : 'Add File'}
        footer={
          <>
            <Button
              icon="check"
              onClick={handleSubmit}
              disabled={isSubmitting || !form.title.trim() || !form.as_of.trim()}
            >
              {isSubmitting ? 'Saving...' : editingId ? 'Update File' : 'Add File'}
            </Button>
            <Button variant="ghost" onClick={closePanel} disabled={isSubmitting}>
              Cancel
            </Button>
          </>
        }
      >
        <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && (
            <div
              style={{
                padding: '10px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                border: '1px solid rgba(229, 72, 77, 0.25)',
                color: 'var(--danger-400)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
            </div>
          )}

          <Input
            label="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Kickoff notes, brand voice, Q3 recap…"
          />

          <Input
            label="As of"
            type="date"
            required
            value={form.as_of}
            onChange={(e) => setForm({ ...form, as_of: e.target.value })}
          />
          <div style={{ marginTop: '-10px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
            When it was said — not when it was uploaded.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FieldMenu
              label="Kind"
              value={form.kind}
              options={KB_KINDS}
              onChange={(kind) => setForm({ ...form, kind })}
            />
            <FieldMenu
              label="Status"
              value={form.status}
              options={KB_STATUSES}
              onChange={(status) => setForm({ ...form, status })}
            />
          </div>

          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Body</div>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={12}
              placeholder="Markdown is fine…"
              className="ck-textarea"
            />
          </div>
        </div>
      </SidePanel>

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        title="Delete File"
        message={
          deleteTarget
            ? `Delete "${deleteTarget.title}"? This cannot be undone.`
            : 'Delete this file? This cannot be undone.'
        }
        confirmButtonLabel="Delete"
      />
    </Card>
  );
}
